/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ComponentChildren, RefObject } from 'preact';
import { useLayoutEffect } from 'preact/hooks';
import { getClientHints } from '../utils/PRIVATE_clientHints';
import { classNames } from '../utils/UNSAFE_classNames';
import { tableRowStyles } from '../PRIVATE_Table/themes/TableRowStyles.css';
import { TableNoDataVariantOptions } from '../PRIVATE_Table/themes/TableNoDataStyles.css';
import { TableNoDataRedwoodTheme } from '../PRIVATE_Table/themes/redwood/TableNoDataTheme';
import { TABLE_NODATA_SELECTOR } from '#PRIVATE_Table/utils/TableDomUtils';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { BundleType } from '../resources/nls/bundle';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';

/**
 * Props for the TableDisplayNoData Component
 */
export type TableDisplayNoDataProps = {
  rootRef: RefObject<HTMLDivElement>;
  tableId: string;
  columnsCount: number;
  ariaRowIndex: number;
  renderer?: () => ComponentChildren;
};

/**
 * The internal component used to render an empty table cell in a TableDisplayBody.
 */
export function TableDisplayNoData({
  rootRef,
  tableId,
  columnsCount,
  ariaRowIndex,
  renderer
}: TableDisplayNoDataProps) {
  const browser = getClientHints().browser;
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  const rowClasses = classNames([tableRowStyles.base]);

  const { classes: themeClasses } = useComponentTheme<TableNoDataVariantOptions>(
    TableNoDataRedwoodTheme,
    {
      defaultRenderer: renderer == null ? 'isDefaultRenderer' : 'notDefaultRenderer',
      showFocusRing: 'notShowFocusRing'
    }
  );

  const cellClasses = classNames([themeClasses]);

  function defaultContentRenderer() {
    return translations.collection_noData();
  }

  const contentRenderer = renderer != null ? renderer : defaultContentRenderer;

  // in firefox, table element heights are not stretched unless explicitly set in some cases
  useLayoutEffect(() => {
    if (browser === 'firefox') {
      const rootElement = rootRef.current;
      if (rootElement != null) {
        const tableBodyElement = rootElement.querySelector(
          `[data-oj-table-body='${tableId}']`
        ) as HTMLElement;
        const noDataElement = rootElement.querySelector(
          `[${TABLE_NODATA_SELECTOR}='${tableId}']`
        ) as HTMLElement;
        if (tableBodyElement != null && noDataElement != null) {
          noDataElement.style.height = '';
          noDataElement.style.height = `${tableBodyElement.getBoundingClientRect().height}px`;
        }
      }
    }
  });

  return (
    <tr aria-rowindex={ariaRowIndex} class={rowClasses} role={'row'}>
      <td
        aria-colindex={1}
        class={cellClasses}
        style={browser !== 'firefox' ? 'height: 100%;' : undefined}
        role={'cell'}
        colSpan={columnsCount}
        data-oj-cell-type={'noData'}
        data-oj-table-focusable={tableId}
        data-oj-table-nodata={tableId}>
        {contentRenderer()}
      </td>
    </tr>
  );
}
